import { useState, useCallback, useMemo } from 'react';
import {
  TARGET_SYSTEM_AUTH_COMPATIBILITY,
  AUTH_METHOD_FIELDS,
  DEFAULT_CUSTOM_SCHEDULE,
  SENSITIVE_FIELDS_BY_AUTH_METHOD
} from '../constants';
import {
  initializeFormData,
  isConnectionField,
  validateForm,
  getScenarioFieldDefaults,
  convertCustomScheduleToObject
} from '../utils';
import { transformFormToPayload } from '../utils/transformFormToPayload';
import * as agentScannerService from '~/services/agentScannerService';

/**
 * Checks if a field is sensitive (requires special handling in edit mode)
 * @param {string} field - Field name
 * @param {string} authMethod - Authentication method
 * @returns {boolean} True if field is sensitive
 */
const isSensitiveField = (field, authMethod) => {
  const sensitiveFields = SENSITIVE_FIELDS_BY_AUTH_METHOD[authMethod] || [];

  return sensitiveFields.includes(field);
};

/**
 * Removes old auth-specific fields from state
 */
const removeAuthFields = (state, authMethod) => {
  const fieldsToRemove = AUTH_METHOD_FIELDS[authMethod] || [];
  const cleanedState = { ...state };

  fieldsToRemove.forEach((field) => {
    delete cleanedState[field];
  });

  return cleanedState;
};

/**
 * Handles target system change and returns new state
 */
const handleTargetSystemChange = (prevState, newTargetSystem) => {
  const compatibleAuthMethods =
    TARGET_SYSTEM_AUTH_COMPATIBILITY[newTargetSystem] || [];
  const isCurrentAuthCompatible = compatibleAuthMethods.includes(
    prevState.authMethod
  );
  const newAuthMethod = isCurrentAuthCompatible ? prevState.authMethod : '';

  // Clean up old fields
  const cleanedState = removeAuthFields(prevState, prevState.authMethod);

  // Get scenario-specific field defaults
  const newAuthFields = getScenarioFieldDefaults(
    newTargetSystem,
    newAuthMethod
  );

  return {
    ...cleanedState,
    targetSystem: newTargetSystem,
    authMethod: newAuthMethod,
    ...newAuthFields
  };
};

/**
 * Handles auth method change and returns new state
 */
const handleAuthMethodChange = (prevState, newAuthMethod) => {
  const cleanedState = removeAuthFields(prevState, prevState.authMethod);

  // Get scenario-specific field defaults
  const newAuthFields = getScenarioFieldDefaults(
    prevState.targetSystem,
    newAuthMethod
  );

  return {
    ...cleanedState,
    authMethod: newAuthMethod,
    ...newAuthFields
  };
};

/**
 * Blurs the run-frequency select element after modal closes
 */
const blurRunFrequencySelect = () => {
  setTimeout(() => {
    const selectElement = document.getElementById('run-frequency');

    if (selectElement) {
      selectElement.blur();
    }
  }, 0);
};

/**
 * Processes custom schedule field change
 */
const processCustomScheduleChange = (prevSchedule, field, value) => {
  const newSchedule = {
    ...prevSchedule,
    [field]: value
  };

  // Reset fields when switching to months
  if (field === 'intervalUnit' && value === 'months') {
    newSchedule.intervalNumber = 1;
    newSchedule.repeatDays = [];
  }

  // Clear repeat days when switching away from weeks
  const isSwitchingFromWeeks =
    field === 'intervalUnit' &&
    value !== 'weeks' &&
    prevSchedule.intervalUnit === 'weeks';

  if (isSwitchingFromWeeks) {
    newSchedule.repeatDays = [];
  }

  return newSchedule;
};

/**
 * Custom hook for managing scanner configuration form state and handlers
 *
 * @param {Object} initialData - Initial form data from props
 * @param {string} mode - Form mode ('add' or 'edit')
 * @param {Function} onSubmit - Submit callback
 * @param {Object} context - Additional context for API payload
 * @param {string} context.organizationId - Organization ID
 * @param {string} context.ownerId - Owner ID
 * @param {string} context.businessGroup - Business Group ID
 * @param {string} context.targetSystemId - Target System UUID
 * @returns {Object} Form state and handlers
 */
const useScannerForm = (initialData, mode, onSubmit, context = {}) => {
  // Form data state
  const [formData, setFormData] = useState(() =>
    initializeFormData(initialData, mode)
  );

  // Track which sensitive fields have been modified in edit mode
  // This is used to determine which fields should be sent as null (unchanged) vs actual value (modified)
  const [modifiedSensitiveFields, setModifiedSensitiveFields] = useState(
    new Set()
  );

  // UI state
  const [connectionStatus, setConnectionStatus] = useState('idle');
  const [connectionError, setConnectionError] = useState(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [customSchedule, setCustomSchedule] = useState(DEFAULT_CUSTOM_SCHEDULE);

  // Reset connection status when connection fields change
  const resetConnectionStatusIfNeeded = useCallback(
    (field) => {
      setFormData((prev) => {
        const shouldReset =
          isConnectionField(field, prev.authMethod) &&
          connectionStatus !== 'idle';

        if (shouldReset) {
          setConnectionStatus('idle');
          setConnectionError(null);
        }

        return prev;
      });
    },
    [connectionStatus]
  );

  // Generic change handler for all form fields
  const handleChange = useCallback(
    (field, value) => {
      // Handle target system change
      if (field === 'targetSystem') {
        setFormData((prev) => handleTargetSystemChange(prev, value));
        resetConnectionStatusIfNeeded(field);

        return;
      }

      // Handle auth method change
      if (field === 'authMethod') {
        setFormData((prev) => handleAuthMethodChange(prev, value));
        resetConnectionStatusIfNeeded(field);
        // Reset modified sensitive fields tracking when auth method changes
        setModifiedSensitiveFields(new Set());

        return;
      }

      // Handle custom run frequency
      if (field === 'runFrequency' && value === 'Custom') {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setShowCustomModal(true);
        resetConnectionStatusIfNeeded(field);

        return;
      }

      // Track modifications to sensitive fields in edit mode
      if (mode === 'edit' && isSensitiveField(field, formData.authMethod)) {
        setModifiedSensitiveFields((prev) => {
          const updated = new Set(prev);

          updated.add(field);

          return updated;
        });
      }

      // Handle all other fields
      setFormData((prev) => ({ ...prev, [field]: value }));
      resetConnectionStatusIfNeeded(field);
    },
    [resetConnectionStatusIfNeeded, mode, formData.authMethod]
  );

  // Handle custom schedule modal save
  const handleCustomScheduleSave = useCallback(() => {
    const structuredSchedule = convertCustomScheduleToObject(customSchedule);

    setFormData((prev) => ({ ...prev, runFrequency: structuredSchedule }));
    setShowCustomModal(false);
    blurRunFrequencySelect();
  }, [customSchedule]);

  // Handle custom schedule modal cancel
  const handleCustomScheduleCancel = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      runFrequency: { unit: 'day', interval: 1 }
    }));
    setShowCustomModal(false);
    blurRunFrequencySelect();
  }, []);

  // Handle custom schedule field changes
  const handleCustomScheduleChange = useCallback((field, value) => {
    setCustomSchedule((prev) =>
      processCustomScheduleChange(prev, field, value)
    );
  }, []);

  // Toggle advanced settings accordion
  const toggleAdvancedSettings = useCallback(() => {
    setShowAdvancedSettings((prev) => !prev);
  }, []);

  // Toggle email notifications
  const handleEmailNotificationsToggle = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      emailNotificationsEnabled: !prev.emailNotificationsEnabled
    }));
  }, []);

  // Add email to recipient list
  const handleAddEmail = useCallback((email) => {
    setFormData((prev) => ({
      ...prev,
      recipientEmails: [...prev.recipientEmails, email]
    }));
  }, []);

  // Remove email from recipient list
  const handleRemoveEmail = useCallback((emailToRemove) => {
    setFormData((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.filter(
        (email) => email !== emailToRemove
      )
    }));
  }, []);

  // Handle connection test
  const handleTestConnection = useCallback(async () => {
    setConnectionStatus('checking');
    setConnectionError(null);

    try {
      // Build connection parameters from form data (reuse payload structure)
      const formPayloadData = {
        name: formData.name || 'Test Connection',
        description: '',
        parameters: {
          target: formData.targetSystem,
          method: formData.authMethod,
          // Amazon Bedrock fields
          accessKeyId: formData.accessKeyId,
          secretAccessKey: formData.secretAccessKey,
          awsRegion: formData.awsRegion,
          // Vertex AI fields
          projectId: formData.projectId,
          serviceAccountEmail: formData.serviceAccountEmail,
          privateKey: formData.privateKey,
          location: formData.location,
          // Azure OpenAI fields
          clientId: formData.clientId,
          clientSecret: formData.clientSecret,
          tenantId: formData.tenantId,
          scope: formData.scope
        },
        runPolicy: {
          runFrequency: 'Daily',
          runTime: '2',
          syncReview: 'auto-resolve'
        }
      };

      // Transform to API payload to get connection structure
      const apiPayload = transformFormToPayload(formPayloadData, context);

      // Extract connection parameters for test
      const { connection } = apiPayload;

      // Parse authParameters from string to JSON object
      const authParams = JSON.parse(connection.authParameters);

      // Call test connection service
      const result = await agentScannerService.testConnection({
        targetSystemType: connection.type,
        connectionParams: authParams,
        context
      });

      if (result) {
        setConnectionStatus('success');
      } else {
        setConnectionStatus('error');
        setConnectionError(
          'Connection test failed. Please verify your credentials.'
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Connection test error:', error);
      setConnectionStatus('error');
      setConnectionError(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to test connection. Please try again.'
      );
    }
  }, [formData, context]);

  // Form submission handler
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      // Helper to get sensitive field value:
      // - In edit mode: return null if field wasn't modified (preserves existing secret)
      // - In edit mode: return actual value if field was modified (updates secret)
      // - In add mode: always return actual value
      const getSensitiveFieldValue = (fieldName, fieldValue) => {
        if (mode === 'edit') {
          // If field is sensitive and wasn't modified, send null to preserve existing value
          if (
            isSensitiveField(fieldName, formData.authMethod) &&
            !modifiedSensitiveFields.has(fieldName)
          ) {
            return null;
          }
        }

        // Return actual value for add mode or modified fields in edit mode
        return fieldValue || null;
      };

      // Build form data in the format expected by transformFormToPayload
      const formPayloadData = {
        name: formData.name,
        description: formData.description,
        parameters: {
          target: formData.targetSystem,
          method: formData.authMethod,
          // Include all auth-specific fields with sensitive field handling
          accessKeyId: formData.accessKeyId,
          secretAccessKey: getSensitiveFieldValue(
            'secretAccessKey',
            formData.secretAccessKey
          ),
          awsRegion: formData.awsRegion,
          projectId: formData.projectId,
          serviceAccountEmail: formData.serviceAccountEmail,
          privateKey: getSensitiveFieldValue('privateKey', formData.privateKey),
          location: formData.location,
          clientId: formData.clientId,
          clientSecret: getSensitiveFieldValue(
            'clientSecret',
            formData.clientSecret
          ),
          tenantId: formData.tenantId,
          scope: formData.scope
        },
        runPolicy: (() => {
          // Flatten the schedule structure: extract unit, interval, on from runFrequency
          const schedule = {
            at: formData.at, // Use 'at' in schedule structure
            syncReview: formData.syncReview
          };

          // If runFrequency is an object, flatten it to top level
          if (
            formData.runFrequency &&
            typeof formData.runFrequency === 'object'
          ) {
            const { unit, interval, on } = formData.runFrequency;

            schedule.unit = unit || 'day';
            schedule.interval = String(interval || 1); // Convert to string
            if (on !== undefined) {
              schedule.on = on;
            }
          } else {
            // Fallback for string values (backward compatibility)
            schedule.unit = 'day';
            schedule.interval = '1';
          }

          // Add optional fields if they exist
          if (formData.timeout) {
            schedule.timeout = formData.timeout;
          }
          if (formData.maxRetryAttempts) {
            schedule.maxRetryAttempts = formData.maxRetryAttempts;
          }

          return schedule;
        })(),
        // Include email notification settings
        emailNotificationsEnabled: formData.emailNotificationsEnabled,
        recipientEmails: formData.recipientEmails
      };

      // Transform to API payload format
      const apiPayload = transformFormToPayload(formPayloadData, {
        ownerId: context.ownerId || '',
        businessGroup: context.businessGroup || '',
        orgId: context.organizationId || ''
      });

      // For edit mode, include the ID
      if (mode === 'edit' && (formData.id || initialData?.id)) {
        apiPayload.id = formData.id || initialData?.id;
      }

      if (onSubmit) {
        onSubmit(apiPayload);
      }
    },
    [formData, mode, initialData, onSubmit, context, modifiedSensitiveFields]
  );

  // Form validation
  const isFormValid = useMemo(
    () => validateForm(formData, mode),
    [formData, mode]
  );

  return {
    // State
    formData,
    connectionStatus,
    connectionError,
    showCustomModal,
    showAdvancedSettings,
    customSchedule,
    isFormValid,

    // Handlers
    handleChange,
    handleSubmit,
    handleTestConnection,
    handleCustomScheduleSave,
    handleCustomScheduleCancel,
    handleCustomScheduleChange,
    toggleAdvancedSettings,
    handleEmailNotificationsToggle,
    handleAddEmail,
    handleRemoveEmail
  };
};

export default useScannerForm;
